import React, { useContext, useState } from 'react';
import { Box } from 'grommet';
import SHDButton from '@ui/Button/SHDButton';
import SHDFooter from '@ui/Footer/SHDFooter';
import SHDHeading from '@ui/Heading/SHDHeading';
import SHDInput from '@ui/Input/SHDInput';
import SHDModalContainer from '@ui/ModalContainer/SHDModalContainer';
import SHDImage from '@ui/Image/SHDImage';
import TagInput from '@helpers/TagInput';
import SHDText from '@ui/Text/SHDText';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { useAddTeamPlayer } from '@hooks/useTeam';
import type { ErrorMessage } from '@theme/theme.d';
import { analytics } from '@services/analytics';
import { InviteRoster } from '@features/Onboarding/Onboarding.d';
import { shd } from '@shd/jslib/models';
import { TeamContext } from '@services/Session';
import { toOldTeamPlayer } from '@helpers/utils/userPlayerUtils';
interface Props {
  onTeamPlayerUpdated: (player: shd.TeamPlayer) => void;
  onClose: () => void;
}

const AddNewPlayer: React.FC<Props> = ({ onTeamPlayerUpdated, onClose }) => {
  const error: ErrorMessage = { message: 'Invalid phone number.' };
  const { id, sport } = useContext(TeamContext);
  const [errorSavingPlayer, setErrorSavingPlayer] = useState(false);

  const [isSaving, setIsSaving] = useState(false);
  const [player, setPlayer] = useState<InviteRoster>({
    id: 0,
    hasPhoneError: false,
    hasNameError: true,
    nameFirst: '',
    nameLast: '',
    jerseyNum: '',
    invitePhoneNumberList: [],
  });
  const [createdPlayer, setCreatedPlayer] = useState<boolean>(false);

  const { addTeamPlayer } = useAddTeamPlayer();

  const areAllPhonesValid = (list?: string[]) =>
    (list ?? []).every((p) => (p ? isValidPhoneNumber(p) : true));

  const setField =
    <K extends keyof InviteRoster>(key: K) =>
    (value: InviteRoster[K] extends string ? string : undefined) => {
      setPlayer((prev) => {
        const next = { ...prev } as InviteRoster;

        if (key === 'invitePhoneNumberList') {
          next.invitePhoneNumberList = value as unknown as string[];
          next.hasPhoneError = !areAllPhonesValid(next.invitePhoneNumberList);
        } else {
          // @ts-expect-error generic assignment for controlled fields
          next[key] = value;
        }

        next.hasNameError = !(next.nameFirst?.trim() && next.nameLast?.trim());
        return next;
      });
    };

  const onPhoneNumberChanged = (value: string[]) => {
    setPlayer((prev) => ({
      ...prev,
      invitePhoneNumberList: value,
      hasPhoneError: !areAllPhonesValid(value),
    }));
  };

  const getDisabled = () =>
    player.hasPhoneError ||
    player.hasNameError ||
    !player.nameFirst?.trim() ||
    !player.nameLast?.trim() ||
    //!player.jerseyNum || not required for now
    isSaving;

  const handleAddPlayer = async () => {
    try {
      setIsSaving(true);
      setErrorSavingPlayer(false);
      analytics.track('Player Added', {
        teamId: id,
        sport,
        addedPhoneNumber: player.invitePhoneNumberList?.[0],
        source: 'Manual',
        importSource: '',
      });

      const created = await addTeamPlayer(player, id);

      if (created) {
        const oldPlayer: shd.TeamPlayer = toOldTeamPlayer(
          created,
          id,
          player.invitePhoneNumberList ?? []
        );
        onTeamPlayerUpdated(oldPlayer);
        setCreatedPlayer(true);
      } else {
        setErrorSavingPlayer(true);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddMore = () => {
    setCreatedPlayer(false);
    setPlayer({
      id: 0,
      hasPhoneError: false,
      hasNameError: true,
      nameFirst: '',
      nameLast: '',
      jerseyNum: '',
      invitePhoneNumberList: [],
    });
  };

  const SuccessView = () => (
    <SHDModalContainer>
      <Box pad={{ vertical: 'large' }} align="center" gap="medium">
        <SHDImage
          alt="Success"
          src={'/team/player/check_icon_primary.png'}
          width={104}
          height={92}
        />
        <SHDHeading
          level={3}
          textAlign="center"
          margin={{ top: 'xsmall', bottom: 'small' }}
        >
          {`${player?.nameFirst}`} has been added
        </SHDHeading>
        <Box direction="row" gap="small" width={'100%'}>
          <SHDButton label="Add more" onClick={handleAddMore} secondary />
          <SHDButton primary label="Done" onClick={onClose} />
        </Box>
      </Box>
    </SHDModalContainer>
  );

  return (
    <Box>
      {createdPlayer ? (
        <SuccessView />
      ) : (
        <>
          <SHDModalContainer>
            <Box direction="column" margin={{ bottom: 'large' }}>
              <SHDHeading level={3}>Add new player</SHDHeading>
            </Box>

            <Box pad={{ top: 'xxsmall' }}>
              <Box
                className="player-row"
                margin={{ bottom: 'xsmall' }}
                gap={'small'}
              >
                <SHDInput
                  label="First name"
                  value={player.nameFirst}
                  onChange={setField('nameFirst')}
                />

                <SHDInput
                  label="Last name"
                  value={player.nameLast}
                  onChange={setField('nameLast')}
                />
                <SHDInput
                  label="Jersey number (Optional)"
                  value={player.jerseyNum}
                  onChange={setField('jerseyNum')}
                />
                <Box gap={'xxsmall'} pad={{ top: 'large' }}>
                  <Box direction="row" gap={'xxxsmall'}>
                    <SHDHeading level={5}>Invite player manager</SHDHeading>
                    <SHDText style="subhead" alignSelf="flex-start">
                      (Optional)
                    </SHDText>
                  </Box>
                  <SHDText style="subhead">
                    We&apos;ll text a link to the number provided so they can
                    claim this player and get updates
                  </SHDText>

                  <Box gap="xxsmall">
                    <TagInput
                      value={player.invitePhoneNumberList}
                      onTagAdded={onPhoneNumberChanged}
                      onTagRemoved={onPhoneNumberChanged}
                      limit={5}
                    />
                    {player.hasPhoneError && (
                      <span style={{ fontSize: 12 }}>{error.message}</span>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </SHDModalContainer>

          <SHDFooter>
            <SHDModalContainer>
              {errorSavingPlayer && (
                <Box pad={{ bottom: 'small' }}>
                  <SHDText style="body" color="red">
                    An error occurred try again
                  </SHDText>
                </Box>
              )}

              <SHDButton
                primary
                label={isSaving ? 'Saving...' : 'Add'}
                onClick={handleAddPlayer}
                disabled={getDisabled()}
              />
            </SHDModalContainer>
          </SHDFooter>
        </>
      )}
    </Box>
  );
};

export default AddNewPlayer;
